import { PureComponent } from 'react';
import autoBind from 'react-autobind';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import cloneDeep from 'lodash-es/cloneDeep';
import orderBy from 'lodash-es/orderBy';

import withComponentName from '../../../decorators/withComponentName';

// services
import TopicsSvc from '../../../services/TopicsSvc';
import PresentationsSvc from '../../../services/dbServices/PresentationsSvc';
import StorageSvc from '../../../services/StorageSvc';
import FilingsSvc from '../../../services/dbServices/FilingsSvc';
import StockExchangesSvc from '../../../services/dbServices/StockExchangesSvc';
import DCSCSectorsSvc from '../../../services/dbServices/DCSCSectorsSvc';

// constants
import { ALLOWED_TABS, DEFAULT_TABS, TABS } from '../../../data/directory/topic_classes/constants';
import { DESKTOP, TABLET, MOBILE } from '../../../data/screenSizes';
import {
  CRYPTOCURRENCIES,
  PUBLIC_COMPANIES,
  PRIVATE_COMPANIES,
  COMMODITIES,
  INDICES,
  FOREIGN_EXCHANGE,
  EXCHANGE_TRADED_FUNDS,
  CLOSED_END_FUNDS,
} from '../../../data/directory/topic_classes/topicClassTypes';
import {
  DIRECTORIES_PATH,
  TOPIC_CLASS_BREADCRUMBS,
} from '../../../data/directory/constants';
import {
  DCSC_SECTORS as DCSC_SECTORS_TOPIC_TYPE,
  DCSC_TYPES,
} from '../../../data/directory/topicTypes';
import {
  CHARTS, FILINGS, NEWS,
  INVESTOR_RELATIONS, INFORMATION, ESG,
  INSIDER_TRANSACTIONS,
  CAPITAL_STRUCTURE,
  DCSC_SECTORS, SMART_WATCHLIST,
} from '../../../data/directory/topic_classes/tabPaths';
import { IR, ESG as ESG_FEATURE } from '../../../data/features';

// helpers
import { getTrendingBarAssetQuery } from '../../../helpers/watchlistHelperFunctions';
import { removeSlugFromPath } from '../../../helpers/topicsHelpers';
import { setSEOTopicMetadata } from '../../../helpers/metaDataHelpers';
import { fetchTopicFullDetails } from '../../../helpers/topicsApiHelpers';
import isMarketTime from '../../../helpers/isMarketTime';
import getInsiderTransactionTickers from '../../../helpers/getInsiderTransactionTickers';
import tryInsiderTransactions from '../../../helpers/tryInsiderTransactions';
import { filingsParamGenerate, presentationsParamGenerate } from '../../../helpers/identifierParamHelpers';

import loadable from '../../../utils/loadable';

// components
import Preloader from '../../Preloader';

// actions
import {
  filterByQuery,
  filterByStoryOrder,
  setSentimentTab,
} from '../../../actions/storyFilters.action';
import { loadIRStorageDataIntoRedux } from '../../../actions/investorRelations.actions';
import { loadESGStorageDataIntoRedux } from '../../../actions/esg.actions';

// styles
import Styles from './styles.module.scss';

const TopicHeader = loadable(() => import('./TopicHeader'));
const TopicBody = loadable(() => import('../TopicTabBodyCommon'));

const cx = classNames.bind(Styles);

export class Topic extends PureComponent {
  constructor(props) {
    super(props);
    autoBind(this);

    const { topicSlug } = props;
    this.isSummary = topicSlug.toLowerCase() === 'summary';
    this.state = this.getState();

    this.mounted = true;
    this.ctaPanelTimer = null;
    try {
      this.userSimplifiedView = JSON.parse(StorageSvc.getItem('userSimplifiedView'));
    } catch (e) {
      this.userSimplifiedView = false;
    }
  }

  async componentDidMount() {
    const {
      history, page,
      loadIRStorageDataIntoRedux: propsIRAction,
      loadESGStorageDataIntoRedux: propsESGAction,
      assetType,
    } = this.props;

    await this.fetchSelectedTabs();

    const { tabs: allowedTabs } = ALLOWED_TABS.find(
      ({ type }) => type === assetType,
    ) || DEFAULT_TABS;

    const tabs = (
      TABS
        .filter((tab) => allowedTabs.includes(tab.id))
        .map((tab) => ({ ...tab }))
    );
    const foundID = tabs.find((tab) => (
      tab.path === page
      || tab.path === history.location.hash.slice(1)
    )) || {};

    const sentimentName = history.location.pathname.split('/').reverse()[0];
    this.setState({
      activeTabNumber: foundID.id || 1,
      isAdvancedView: ((['all', 'very_positive', 'positive', 'neutral', 'negative', 'very_negative'].includes(sentimentName)) || this.userSimplifiedView),
    }, () => {
      const { isAdvancedView, activeTabNumber, activeSentimentTab } = this.state;
      setSentimentTab(activeSentimentTab);
      StorageSvc.setItem(
        'selectedSentimentTab',
        JSON.stringify({ activeSentimentTab }),
      );
      const sentimentName = history.location.pathname.split('/').reverse();
      if ((isAdvancedView && activeTabNumber === 1) || activeTabNumber !== 1) {
        if (['all', 'very_positive', 'positive', 'neutral', 'negative', 'very_negative'].includes(sentimentName[0])) {
          history.replace(`${history.location.pathname.split('/').slice(0, -1).join('/')}/${activeSentimentTab}`);
        } else if (isAdvancedView && activeTabNumber === 1) {
          history.replace(`${history.location.pathname}/${activeSentimentTab}`);
        } else {
          history.replace(`${history.location.pathname}`);
        }
      } else if (!isAdvancedView && activeTabNumber === 1) {
        if (['all', 'very_positive', 'positive', 'neutral', 'negative', 'very_negative'].includes(sentimentName[0])) {
          history.replace(`${history.location.pathname.split('/').slice(0, -1).join('/')}`);
        } else {
          history.replace(history.location.pathname);
        }
      }
    });
    window.scrollTo(0, 0);

    await this.prepareData();
    propsIRAction();
    propsESGAction();

    this.onResize();
    // In case if hash changed manually from URL
    window.addEventListener('hashchange', this.changeTabByHash, { passive: true });
  }

  async componentDidUpdate(prevProps) {
    const {
      width, topicSlug, orderTab, permissionsInitialized,
    } = this.props;
    if (prevProps.width !== width) this.onResize();
    if (prevProps.topicSlug !== topicSlug) {
      const { isAdvancedView } = this.state;
      this.setState({
        ...this.getState(),
        isAdvancedView,
      });
      await this.prepareData();
    }
    if (prevProps.orderTab && orderTab && (orderTab !== prevProps.orderTab)) {
      this.setState({
        activeOrderTab: orderTab,
      }, () => {
        const { activeOrderTab } = this.state;
        this.handleOrderTabClick(activeOrderTab);
      });
    }

    const { topic } = this.state;
    if (topic && permissionsInitialized !== prevProps.permissionsInitialized) {
      this.getCapitalStructure(topic);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.ctaPanelTimer) clearTimeout(this.ctaPanelTimer);
    window.removeEventListener('hashchange', this.changeTabByHash);
  }

  onResize() {
    const { width } = this.props;
    let mode;

    switch (true) {
      case width > 1023:
        mode = DESKTOP;
        break;
      case width > 767:
        mode = TABLET;
        break;
      default:
        mode = MOBILE;
        break;
    }

    this.setState({ mode });
  }

  async getAssetTabData(topic) {
    const availableTickersArr = topic?.tickers?.filter((ticker) => ticker.default) || [];

    // eslint-disable-next-line no-param-reassign
    topic.mainTicker = availableTickersArr[0]?.full_name;

    const state = {
      topic,
    };

    state.isInfoTab = !!((topic.legal_entities || []).length && (topic.tickers || []).length);

    const { activeTabNumber } = this.state;
    if (
      activeTabNumber === 1 || activeTabNumber === 2
      || activeTabNumber === 5
    ) {
      state.isGettingData = false;
    }

    return state;
  }

  getAllTabsData() {
    const { assetType, slugType } = this.props;
    const { topic } = this.state;
    if (PUBLIC_COMPANIES === assetType || PRIVATE_COMPANIES === assetType) {
      this.checkPresentationsData(topic);
      this.getFilingsData(topic);
      this.getCapitalStructure(topic);
    }
    if (
      assetType === PUBLIC_COMPANIES || assetType === CRYPTOCURRENCIES
      || assetType === EXCHANGE_TRADED_FUNDS || assetType === COMMODITIES
      || assetType === FOREIGN_EXCHANGE || assetType === INDICES
    ) {
      this.getPricingChartData(topic);
    }
    if (
      PUBLIC_COMPANIES === assetType
      || PRIVATE_COMPANIES === assetType
      || EXCHANGE_TRADED_FUNDS === assetType
      || CLOSED_END_FUNDS === assetType
    ) {
      this.checkInsiderTransactionsExistence(topic);
    }
    if (slugType !== DCSC_SECTORS_TOPIC_TYPE) {
      this.checkDCSCSectorsData();
    }
    this.getAllTabs();
  }

  getAllTabs() {
    const {
      isFilingsTab, isIRTab, isESGTab,
      isChartTab, isInfoTab, isCapitalStructureTab,
      defaultAllTabs, topic, isGettingData, activeTabNumber, isMetaSet,
      insiderTransactionsExists, dcscSectorsExists,
    } = this.state;
    const { location, slugType } = this.props;
    let newTabs = cloneDeep(defaultAllTabs);

    if (!isFilingsTab) {
      newTabs = newTabs.filter((tab) => tab.path !== FILINGS);
    }
    if (!isIRTab) {
      newTabs = newTabs.filter((tab) => tab.path !== INVESTOR_RELATIONS);
    }
    if (!isESGTab) {
      newTabs = newTabs.filter((tab) => tab.path !== ESG);
    }
    if (!isChartTab) {
      newTabs = newTabs.filter((tab) => tab.path !== CHARTS);
    }
    if (!isInfoTab) {
      newTabs = newTabs.filter((tab) => (
        tab.path !== INFORMATION
      ));
    }
    if (!insiderTransactionsExists) {
      newTabs = newTabs.filter((tab) => (
        tab.path !== INSIDER_TRANSACTIONS
      ));
    }
    if (!dcscSectorsExists) {
      newTabs = newTabs.filter((tab) => (
        tab.path !== DCSC_SECTORS
      ));
    }
    if (!isCapitalStructureTab) {
      newTabs = newTabs.filter((tab) => tab.path !== CAPITAL_STRUCTURE);
    }
    if (slugType !== DCSC_SECTORS_TOPIC_TYPE) {
      newTabs = newTabs.filter((tab) => tab.path !== SMART_WATCHLIST);
    }

    if (!isGettingData) {
      const { page, history } = this.props;
      const tabDetails = newTabs.find((tab) => tab.id === activeTabNumber);
      if (!newTabs.some((tab) => tab.active) && !tabDetails) {
        const newsTab = newTabs.find((tab) => tab.path === NEWS);
        if (newsTab) {
          newsTab.active = true;
        }
        if (!this.isSummary) {
          history.replace(`${page ? removeSlugFromPath(location.pathname) : location.pathname}/${NEWS}`);
        }
      }

      // Set title and metatag title
      const { assetType } = this.props;
      if (
        (
          PUBLIC_COMPANIES === assetType
          || PRIVATE_COMPANIES === assetType
          || CRYPTOCURRENCIES === assetType
          || DCSC_TYPES.includes(assetType)
        ) && !isMetaSet
      ) {
        const ticker = topic?.tickers?.find((f) => f.default)?.name;
        const { path } = newTabs.find((tab) => tab.active);
        const assetNumber = topic?.legal_entities?.length
          ? (topic.legal_entities.find((f) => f.legal_entity_type === 'companieshouse-gb' || topic.legal_entities[0]).legal_entity_id)
          : '';
        setSEOTopicMetadata({
          activeTab: path,
          topicName: topic.name,
          topicType: assetType,
          figis: topic.figis,
          topicTicker: (ticker || '').toUpperCase(),
          assetNumber,
        });
        this.setState({
          isMetaSet: true,
        });
      }
    }

    this.setState(() => ({
      tabs: newTabs,
    }));
  }

  async getSectorData() {
    const { assetType, topicSlug } = this.props;

    const topic = await DCSCSectorsSvc.getLevelTopic(topicSlug, assetType);

    if (!topic) {
      window.history.replaceState({}, '', '/404');
      window.location.reload();
      return;
    }

    return { topic };
  }

  async getAssetData() {
    try {
      const {
        topicSlug,
        slugName,
        assetType,
        location,
      } = this.props;
      const topic = !this.isSummary ? await (
        TopicsSvc.getTopicBySlug(topicSlug)
      ) : null;
      const assetClass = this.isSummary ? (
        await TopicsSvc.getTopicClassBySlug(slugName)
      ) : (topic?.asset_class);

      if (
        (this.isSummary && !assetClass) || (!this.isSummary && !topic)
        || !location.pathname.startsWith(`${DIRECTORIES_PATH}/${assetClass.slug}`)
      ) {
        window.location.href = '/404';
        return;
      }

      const breadcrumbs = TOPIC_CLASS_BREADCRUMBS.concat([
        {
          name: assetClass.title,
          path: slugName,
        },
        {
          name: this.isSummary ? 'Summary' : topic.name,
          path: `${slugName}/${topicSlug}`,
        },
      ]);

      this.setState({
        topic,
        breadcrumbs,
      });

      // Set title and meta tag title
      if (
        (assetType || this.isSummary)
        && PUBLIC_COMPANIES !== assetType
        && PRIVATE_COMPANIES !== assetType
        && CRYPTOCURRENCIES !== assetType
      ) {
        setSEOTopicMetadata({
          topicName: topic.name,
          topicType: assetClass.title,
          slugName,
          isSummary: this.isSummary,
        });
      }

      return {
        assetClass,
        topic,
      };
    } catch (e) {
      console.log(e);
      return {};
    }
  }

  getState() {
    const {
      assetType,
      location,
      page,
      history,
    } = this.props;

    const { tabs: allowedTabs } = ALLOWED_TABS.find(
      ({ type }) => type === assetType,
    ) || DEFAULT_TABS;

    const tabs = (
      TABS
        .filter((tab) => allowedTabs.includes(tab.id))
        .map((tab) => ({ ...tab }))
    );
    let activeTab = tabs.find((tab) => (
      tab.path === page
      || tab.path === location.hash.slice(1)
    ));
    if (!activeTab) {
      activeTab = tabs.find((tab) => tab.path === NEWS) || tabs[0];
      if (!this.isSummary) {
        history.replace(`${page ? removeSlugFromPath(location.pathname) : location.pathname}/${NEWS}`);
      }
    }

    activeTab.active = true;

    return {
      tabs: tabs.filter((f) => f.isDefault),
      defaultAllTabs: tabs,
      assetClass: null,
      topic: null,
      breadcrumbs: [],
      mode: DESKTOP,
      loading: true,
      activeOrderTab: 'top',
      activeSentimentTab: 'all',
      isAdvancedView: true,
      activeTabNumber: activeTab.id,
      isGettingData: true,
      isMetaSet: false,
      insiderTransactionsExists: false,
      dcscSectorsExists: false,
    };
  }

  async getInvestorRelationsData(param) {
    const { permissions } = this.props;
    const { activeTabNumber } = this.state;
    const hasIRAccess = (permissions.features || []).includes(IR);
    if (!hasIRAccess) {
      return this.setState({
        isIRTab: false,
        ...(activeTabNumber === 6 ? { isGettingData: false } : {}),
      }, () => {
        this.getAllTabs();
      });
    }

    let data = [];
    try {
      ({ data } = await PresentationsSvc.getPresentationsData(param));
    } catch (err) { }

    return this.setState({
      isIRTab: (data || []).length,
      ...(activeTabNumber === 6 ? { isGettingData: false } : {}),
    }, () => {
      this.getAllTabs();
    });
  }

  async getESGData(param) {
    const { permissions } = this.props;
    const { activeTabNumber } = this.state;
    const hasIRAccess = (permissions.features || []).includes(ESG_FEATURE);
    if (!hasIRAccess) {
      return this.setState({
        isESGTab: false,
        ...(activeTabNumber === 7 ? { isGettingData: false } : {}),
      }, () => {
        this.getAllTabs();
      });
    }

    let data = [];
    try {
      ({ data } = await PresentationsSvc.getPresentationsEsgData(param));
    } catch (err) { }

    return this.setState({
      isESGTab: (data || []).length,
      ...(activeTabNumber === 7 ? { isGettingData: false } : {}),
    }, () => {
      this.getAllTabs();
    });
  }

  async getFilingsData(topic) {
    const { activeTabNumber } = this.state;
    if (!topic.legal_entities?.length && !topic.tickers?.length) {
      this.setState({
        isFilingsTab: false,
        ...(activeTabNumber === 2 ? { isGettingData: false } : {}),
      }, this.getAllTabs);
      return;
    }

    const { assetType } = this.props;
    const identifier = filingsParamGenerate(topic, assetType);
    let data;
    if (identifier) {
      try {
        ({ data } = await FilingsSvc.getFillings({
          param: {
            identifier,
            per: 1,
          },
        }));
      } catch (err) {
        data = [];
      }
    }

    this.setState({
      isFilingsTab: !!data?.length,
      ...(activeTabNumber === 2 ? { isGettingData: false } : {}),
    }, this.getAllTabs);
  }

  async getPricingChartData(topic) {
    const { activeTabNumber } = this.state;
    const topicTickerArr = orderBy(topic?.tickers?.filter((ticker) => ticker.default), 'priority', 'asc');

    const { assetType } = this.props;
    const params = {
      page_limit: 1,
      page: 1,
    };
    let hasChartData;

    for (let i = 0; i < topicTickerArr.length; i += 1) {
      const currentTopic = topicTickerArr[i];
      if (assetType === COMMODITIES || assetType === INDICES || assetType === FOREIGN_EXCHANGE) {
        params.identifiers = `TR:${currentTopic.name}`;
      } else if (assetType === CRYPTOCURRENCIES) {
        params.symbol = currentTopic.name;
      } else {
        params.symbol = currentTopic.full_name;
      }

      const stockExchangeMarketHours = currentTopic?.stock_exchange_id ? (
        // eslint-disable-next-line no-await-in-loop
        await StockExchangesSvc.getStockExchangeMarketHours(
          currentTopic.stock_exchange_id,
        )
      ) : null;
      if (stockExchangeMarketHours) {
        params.market_hours = isMarketTime(stockExchangeMarketHours);
      }

      // eslint-disable-next-line no-await-in-loop
      const { fullDetails, error } = await fetchTopicFullDetails(assetType, params);
      if (fullDetails && !error) {
        hasChartData = true;
        break;
      }
    }

    this.setState({
      isChartTab: topic.trading_view_ticker || !!hasChartData,
      hasHeaderPricingChart: !!hasChartData,
      ...(activeTabNumber === 4 ? { isGettingData: false } : {}),
    }, this.getAllTabs);
  }

  getCapitalStructure(topic) {
    const { activeTabNumber } = this.state;

    const isCapitalStructureTab = !!(topic.legal_entities || []).length;
    this.setState({
      isCapitalStructureTab,
      ...(activeTabNumber === 9 ? { isGettingData: false } : {}),
    }, this.getAllTabs);
  }

  async checkInsiderTransactionsExistence(topic) {
    const { activeTabNumber } = this.state;

    const usTickers = getInsiderTransactionTickers(topic);
    if (!usTickers?.length) {
      return this.setState({
        insiderTransactionsExists: false,
        ...(activeTabNumber === 8 ? { isGettingData: false } : {}),
      }, this.getAllTabs);
    }

    const { transactions } = await tryInsiderTransactions(usTickers);

    this.setState({
      insiderTransactionsExists: !!transactions.length,
      ...(activeTabNumber === 8 ? { isGettingData: false } : {}),
    }, this.getAllTabs);
  }

  async checkDCSCSectorsData() {
    const { activeTabNumber } = this.state;
    const { topic } = this.state;
    const { relevances: response } = await DCSCSectorsSvc.getRelevantCompanies({
      topicId: topic.id,
    });

    this.setState({
      dcscSectorsExists: !!response.length,
      ...(activeTabNumber === 11 ? { isGettingData: false } : {}),
    }, this.getAllTabs);
  }

  checkPresentationsData(topic) {
    const identifiers = presentationsParamGenerate(topic);
    if (!identifiers) {
      const { activeTabNumber } = this.state;
      return this.setState({
        isIRTab: false,
        isESGTab: false,
        ...(activeTabNumber === 6 || activeTabNumber === 7 ? { isGettingData: false } : {}),
      }, () => {
        this.getAllTabs();
      });
    }

    const param = {
      identifiers,
      per: 1,
    };

    this.getInvestorRelationsData(param);
    this.getESGData(param);
  }

  handleOrderTabClick(activeOrderTab) {
    const {
      filterByStoryOrder,
      userToken,
      piwikEnabled,
    } = this.props;
    StorageSvc.setItem(
      'selectedOrderTab',
      JSON.stringify({ activeOrderTab }),
    );
    if (piwikEnabled) _paq.push(['trackEvent', 'Filters', 'Story order', activeOrderTab]);
    this.setState({
      activeOrderTab,
    }, () => {
      const { activeOrderTab } = this.state;
      filterByStoryOrder(activeOrderTab, userToken);
    });
  }

  handleSentimentTabClick(activeSentimentTab) {
    const {
      setSentimentTab, history,
    } = this.props;
    const sentimentName = history.location.pathname.split('/').reverse();
    setSentimentTab(activeSentimentTab);
    StorageSvc.setItem(
      'selectedSentimentTab',
      JSON.stringify({ activeSentimentTab }),
    );
    this.setState({
      activeSentimentTab,
    }, () => {
      const { activeSentimentTab, activeTabNumber } = this.state;
      if (activeSentimentTab) {
        if (['all', 'very_positive', 'positive', 'neutral', 'negative', 'very_negative'].includes(sentimentName[0]) && activeTabNumber === 1) {
          history.replace(`${history.location.pathname.split('/').slice(0, -1).join('/')}/${activeSentimentTab}`);
        } else {
          history.replace(history.location.pathname);
        }
      }
    });
  }

  handleSimplifiedViewTabsClick(show) {
    const {
      history,
      filterByStoryOrder,
      userToken,
    } = this.props;
    this.setState({
      isAdvancedView: show,
    }, () => {
      const { isAdvancedView, activeSentimentTab, activeOrderTab } = this.state;
      const sentimentName = history.location.pathname.split('/').reverse()[0];
      if (isAdvancedView) {
        const path = (['all', 'very_positive', 'positive', 'neutral', 'negative', 'very_negative'].includes(sentimentName)) ? `${history.location.pathname.split('/').slice(0, -1).join('/')}/${activeSentimentTab}` : `${history.location.pathname}/${activeSentimentTab}`;
        history.replace(path);
        filterByStoryOrder(activeOrderTab, userToken);
        StorageSvc.setItem(
          'selectedOrderTab',
          JSON.stringify({ activeOrderTab }),
        );
      } else {
        const path = (['all', 'very_positive', 'positive', 'neutral', 'negative', 'very_negative'].includes(sentimentName)) ? `${history.location.pathname.split('/').slice(0, -1).join('/')}` : history.location.pathname;
        history.replace(path);
      }
    });
  }

  fetchSelectedTabs() {
    const {
      history,
      filterByStoryOrder,
      userToken,
      filtersInitialized,
    } = this.props;
    const getSelectedOrderTab = JSON.parse(StorageSvc.getItem('selectedOrderTab'));
    const getSelectedSentimentTab = JSON.parse(StorageSvc.getItem('selectedSentimentTab'));
    const sentimentName = history.location.pathname.split('/').reverse()[0];
    if (getSelectedOrderTab?.activeOrderTab) {
      this.setState({
        activeOrderTab: getSelectedOrderTab?.activeOrderTab || 'top',
      }, () => {
        const { activeOrderTab } = this.state;
        const loadFilters = !filtersInitialized;
        filterByStoryOrder(activeOrderTab, userToken, loadFilters);
      });
    }
    if (['all', 'very_positive', 'positive', 'neutral', 'negative', 'very_negative'].includes(sentimentName)) {
      this.setState({
        activeSentimentTab: sentimentName,
      }, () => {
        const { activeSentimentTab } = this.state;
        StorageSvc.setItem(
          'selectedSentimentTab',
          JSON.stringify({ activeSentimentTab }),
        );
      });
    } else if (getSelectedSentimentTab?.activeSentimentTab) {
      this.setState({
        activeSentimentTab: getSelectedSentimentTab?.activeSentimentTab || 'all',
      });
    }
  }

  changeTabByHash() {
    const { location: { hash }, page } = this.props;
    let { tabs } = this.state;
    const path = hash.slice(1);

    if (page) return;

    if (tabs.find((tab) => tab.active).path === path) return;

    tabs = tabs.map((tab) => ({
      ...tab,
      active: tab.path === path,
    }));

    if (!tabs.some((tab) => tab.active)) {
      const newsTab = tabs.find((tab) => tab.path === NEWS);
      if (newsTab) {
        newsTab.active = true;
        window.location.hash = `#${NEWS}`;
      }
    }
    // Set title and metatag title
    const { assetType } = this.props;
    if (
      PUBLIC_COMPANIES === assetType
      || PRIVATE_COMPANIES === assetType
      || CRYPTOCURRENCIES === assetType
      || DCSC_TYPES.includes(assetType)
    ) {
      const { topic } = this.state;
      const ticker = topic?.tickers?.find((f) => f.default)?.name;
      const assetNumber = topic?.legal_entities?.length
        ? (topic.legal_entities.find((f) => f.legal_entity_type === 'companieshouse-gb' || topic.legal_entities[0]).legal_entity_id)
        : '';
      setSEOTopicMetadata({
        activeTab: path,
        topicName: topic.name,
        topicType: assetType,
        figis: topic.figis,
        topicTicker: (ticker || '').toUpperCase(),
        assetNumber,
      });
    }

    this.setState({ tabs });
  }

  scrollTo(offset) {
    return new Promise((resolve) => {
      const onScroll = () => {
        // Mark scroll as ended if scroll position near element
        if (Math.abs(window.scrollY - offset) < 20) {
          window.removeEventListener('scroll', onScroll);
          resolve();
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      window.scrollTo({
        top: offset,
        behavior: 'smooth',
      });
    });
  }

  changeTab(id, offset) {
    const { piwikEnabled } = this.props;
    if (!offset) {
      let { tabs } = this.state;
      const { page, history, location } = this.props;
      tabs = tabs.map((tab) => ({
        ...tab,
        active: tab.id === id,
      }));

      const { isAdvancedView, activeSentimentTab } = this.state;
      const { id: tabId, path } = tabs.find((tab) => tab.active);
      if (page) {
        if (isAdvancedView && tabId === 1) {
          history.push(`${removeSlugFromPath(location.pathname)}/${path}/${activeSentimentTab}`);
        } else {
          history.push(`${removeSlugFromPath(location.pathname)}/${path}`);
        }
      } else {
        window.location.hash = path;
      }

      // Set title and metatag title
      const { assetType } = this.props;
      if (
        PUBLIC_COMPANIES === assetType
        || PRIVATE_COMPANIES === assetType
        || CRYPTOCURRENCIES === assetType
        || DCSC_TYPES.includes(assetType)
      ) {
        const { topic } = this.state;
        const ticker = topic?.tickers?.find((f) => f.default)?.name;
        const assetNumber = topic?.legal_entities?.length
          ? (topic.legal_entities.find((f) => f.legal_entity_type === 'companieshouse-gb' || topic.legal_entities[0]).legal_entity_id)
          : '';
        setSEOTopicMetadata({
          activeTab: path,
          topicName: topic.name,
          topicType: assetType,
          figis: topic.figis,
          topicTicker: (ticker || '').toUpperCase(),
          assetNumber,
        });
      }

      this.setState({ tabs, activeTabNumber: id });
      if (piwikEnabled) {
        _paq.push(['trackEvent', 'Tabs', `Tab: ${id}`]);
      }
      return;
    }

    this.scrollTo(offset).then(() => {
      this.changeTab(id, null);
    });
  }

  async prepareData() {
    const { setTopic, slugType } = this.props;

    const {
      assetClass,
      topic,
    } = await (
      slugType === DCSC_SECTORS_TOPIC_TYPE
        ? this.getSectorData()
        : this.getAssetData()
    ) || {};

    if (this.mounted && (assetClass || topic)) {
      if (topic) {
        setTopic(
          topic,
          slugType === DCSC_SECTORS_TOPIC_TYPE
            ? topic.type
            : 'Topic',
        );
      }
      const state = {
        assetClass,
        topic,
      };

      const newState = await this.getAssetTabData(topic);

      return new Promise((resolve) => {
        this.setState({ ...state, ...newState }, () => {
          this.getAllTabsData();
          resolve();
        });
      });
    }
  }

  render() {
    const {
      breadcrumbs,
      topic,
      mode,
      tabs,
      trustpilot,
      assetClass,
      activeOrderTab,
      activeSentimentTab,
      isAdvancedView,
      activeTabNumber,
      hasHeaderPricingChart,
    } = this.state;
    const {
      assetType,
      slugType,
      user,
      blurSearchbar,
      searchBarActive,
      piwikEnabled,
      width,
      isChatbotOpened,
    } = this.props;

    const isTabletViewWhileChatbotIsOpened = isChatbotOpened
    && (width > 1170 && width <= 1500);

    return (
      <main className={cx('topic-wrapper', { blur: blurSearchbar && searchBarActive })}>
        <Preloader loading={!topic} static centered />
        {topic && (
          <article className={cx('topic')}>
            <TopicHeader
              topic={this.isSummary ? {
                ...assetClass,
                name: assetClass.title,
              } : topic}
              isSummary={this.isSummary}
              breadcrumbs={breadcrumbs}
              assetType={assetType}
              mode={mode}
              user={user}
              minigraph={slugType !== DCSC_SECTORS_TOPIC_TYPE}
              changeTab={this.changeTab}
              hasHeaderPricingChart={hasHeaderPricingChart}
            />
            <TopicBody
              assetType={assetType}
              slugType={slugType}
              tabs={tabs}
              changeTab={this.changeTab}
              user={user}
              slug={this.isSummary ? assetClass : topic}
              mode={mode}
              trustpilot={trustpilot}
              isSummary={this.isSummary}
              userSimplifiedView={this.userSimplifiedView}
              handleOrderTabClick={this.handleOrderTabClick}
              handleSentimentTabClick={this.handleSentimentTabClick}
              handleSimplifiedViewTabsClick={this.handleSimplifiedViewTabsClick}
              activeOrderTab={activeOrderTab}
              activeSentimentTab={activeSentimentTab}
              isAdvancedView={isAdvancedView}
              activeTab={activeTabNumber}
              piwikEnabled={piwikEnabled}
              isSEOPage
              fromTopicComponent
              isTabletViewWhileChatbotIsOpened={isTabletViewWhileChatbotIsOpened}
            />
          </article>
        )}
      </main>
    );
  }
}

function mapToStateProps({
  watchlistReducer, common, subscriptions, searchReducers, storyFilters,
  chatbot,
}) {
  return {
    userToken: watchlistReducer.userToken,
    width: common.width,
    permissionsInitialized: subscriptions.permissionsInitialized,
    permissions: subscriptions.permissions,
    searchBarActive: searchReducers.searchBarActive,
    blurSearchbar: searchReducers.blur,
    orderTab: storyFilters.order_by,
    filtersInitialized: storyFilters.filtersInitialized,
    isChatbotOpened: chatbot.isChatbotOpened,
  };
}

function mapToDispatchProps(dispatch) {
  return {
    setTopic: (topic, type = 'Topic') => dispatch(filterByQuery(getTrendingBarAssetQuery(topic, type))),
    loadIRStorageDataIntoRedux: () => dispatch(loadIRStorageDataIntoRedux()),
    loadESGStorageDataIntoRedux: () => dispatch(loadESGStorageDataIntoRedux()),
    filterByStoryOrder:
      (activeOrderTab, token, loadFilters) => dispatch(filterByStoryOrder(activeOrderTab, token, loadFilters)),
    setSentimentTab:
      (activeSentimentTab) => dispatch(setSentimentTab(activeSentimentTab)),
  };
}

export default withComponentName(
  connect(
    mapToStateProps,
    mapToDispatchProps,
  )(withRouter(Topic)),
);
